import * as THREE from "three";
import { Enemy, EnemyArchetype } from "@highmarch/domain";
import { Palette } from "../scene";
import { AnimRig } from "../rig";
import { HQ_YAW, capsule, hqEnemyName, hqNode, lib, mat } from "./common";
import { RING } from "./blobShadow";
import { bossRingWidth, headTop } from "./bossSize";
import { BarState } from "./hpBars";
import { UnitLook, archScale, dissolveEdge } from "../unitFx";
import { NOT_WAITING, emptyMarks } from "../statusFx";
import { BossStage } from "./bossStage";
import { EnemyLook, auraRadius, bossRim } from "./enemyLook";
import { DIE, Death, EnemyView } from "./enemyView";
import { wash } from "./enemyFeel";
import { enemyArtId, rareScale } from "./enemyStandIns";
import { WYRM_CLIPS } from "./wyrmView";
import { ENEMY_GAIT, coarseWalk, drawnStride, walkGaitOf } from "./enemyGait";
import { bodyTilt, groundTrack, resetTrack } from "./groundFollow";
import { FootIk } from "./footIk";

/** Enemy views made and readied (enemies.ts EnemyViews pools them). */

/** A model's half-size (world units, before its scale) when its anchors do not give one. */
const FOOT_FALLBACK = 0.1;

/** A new pooled view for `e` wearing `model` (its root is the caller's to add to the scene). */
export function buildEnemyView(e: Enemy, archId: string, model: string, role: EnemyLook, key: string, heightOf: (model: string, hq: boolean) => number, stage: BossStage, rare = 0, wyrm = false, wild = false): EnemyView {
  const root = new THREE.Group();
  // M36: a rare is drawn as a boss (its moment, ring and DOM bar) whatever the sim flags it; `rare` is its chapter 1–6.
  // Pets v2: the wild pup is a boss to the sim only (targeting, control): its bar and moments are level/levelPet.ts's
  const boss = (e.isBoss && !wild) || rare > 0;
  const L = lib();
  const hq = model.startsWith("hq:");
  let body: THREE.Object3D;
  if (L && hq) { body = L.clone(model); body.rotation.order = "YXZ"; body.rotation.y = HQ_YAW; }
  else if (L) body = new THREE.Group();                 // its model is not in: nothing is drawn (marks and bar still are)
  else { body = new THREE.Mesh(capsule, mat(boss ? Palette.boss : Palette.grunt)); body.position.y = 0.4; }
  const name = model.replace(/^hq:/, "").replace(/_lod1$/, "");   // node names are the full model's
  const anchor = hq ? L?.anchors.get(model) ?? L?.anchors.get(name) : undefined;
  const height = anchor?.height ?? 0.33;
  // Size by role (enemyLook.ts); a boss keeps the art pass's per-boss scale (units F2), which its moment is staged at.
  const scale = hq ? (wyrm ? WYRM_CLIPS.scale : rare > 0 ? rareScale(archId) : boss ? archScale(archId) : role.scale) : body.scale.x;
  const team = boss ? "boss" : "enemy";
  const look = new UnitLook(body, team, height, dissolveEdge(archId, team));
  // the ring hugs the unit (about its own height across); bosses get the wide rune ring
  // a boss model's ring hugs its footprint and its plate rides over its head, not its banner or staff (bossSize.ts)
  const bossModel = hq && boss && rare === 0 && archId.startsWith("boss_");
  const ringWidth = hq ? (bossModel ? bossRingWidth(anchor, height, scale) : boss ? Math.max(RING.bossWidth, height * scale * 1.1) : Math.max(0.24, height * scale)) : boss ? 0.5 : 0.22;
  const head = bossModel ? headTop(body, hqNode(body, name, "head")) : null;
  root.add(body);
  const v: EnemyView = {
    root, body, rig: new AnimRig(body, L && hq ? L.clipsOf(model) : []), hq, model, archId, boss, air: e.isAir, key, look,
    torso: hq ? hqNode(body, name, "body") : null, bomb: hq ? hqNode(body, name, "bomb") : null,
    scale, height, bodyH: head ?? height, ringWidth, barY: hq ? height * scale + 0.1 : 1.0, bar: new BarState(), lastHp: 0, maxHp: 1,
    prev: new THREE.Vector3(), cur: new THREE.Vector3(), phase: Math.random() * 6.28, speed: 0,
    // the gait is the full model's (an LOD twin plays the same clip)
    stride: hq ? drawnStride(walkGaitOf(L?.anchors.get(name)?.gait), scale) : 0, coarse: hq && coarseWalk(walkGaitOf(L?.anchors.get(name)?.gait)), turnHeld: false, path: { x: 0, z: 0, head: NaN, rate: ENEMY_GAIT.turnRate, sx: 0, sz: 0, set: false }, walked: Math.random(), lastX: NaN, lastZ: NaN, run: 0, sinceShot: Infinity,
    knock: { x: 0, v: 0 }, kdx: 0, kdz: 0, stretch: 0, age: 0,
    dead: 0, death: Death.Topple, dissolveAt: 0, dissolveFor: DIE.dissolve, fly: { x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, bounced: false, rest: false }, spinAxis: new THREE.Vector3(1, 0, 0), spinRate: 0, fallFrom: 0,
    bs: null,
    role, colour: new THREE.Color(), aura: 0, headY: (head ?? heightOf(model, hq)) * (hq ? scale : role.scale), headRest: 0, headNow: 0, hp: -1, s1: -1, s2: -1,
    marks: emptyMarks(), waits: [NOT_WAITING, NOT_WAITING, NOT_WAITING],
    owed: 0, simHp: 0, runHp: 0, doomed: false, ghost: { dist: 0, step: 0 },
    buried: 0, rare, imm: 0, wyrm,
    // the soles' footprint: the model's half-depth and half-width at its draw scale; a boss or a rare tilts with the slope (groundFollow.ts bodyTilt)
    foot: groundTrack((anchor?.half_z ?? FOOT_FALLBACK) * scale, Math.min(anchor?.half_x ?? FOOT_FALLBACK, anchor?.half_z ?? FOOT_FALLBACK) * scale,
      bodyTilt(boss && !e.isAir, 2 * (anchor?.half_z ?? FOOT_FALLBACK), height)),
    ik: L && hq && !e.isAir ? FootIk.attach(body, name) : null,
  };
  v.headRest = v.headY;
  if (boss) v.bs = stage.create(root, body, look);
  return v;
}

/** Readies a view (new or from the pool) for a fresh spawn of `e`. */
export function resetEnemyView(v: EnemyView, e: Enemy, arch: EnemyArchetype, role: EnemyLook): void {
  v.archId = arch.id;
  v.role = role;
  v.marks.frost = 0; v.marks.frozen = false; v.marks.burn = 0; v.marks.shred = 0; v.marks.seed = Math.random();
  v.waits.fill(NOT_WAITING);
  v.colour.setHex(role.colour);
  v.aura = auraRadius(arch);
  v.hp = -1;
  if (v.hq) wash(v.body, arch);
  v.root.visible = true;
  v.body.visible = true;                                 // a body hidden for a stand-in (a sheep, the wild pup) shows again
  v.headY = v.headRest;
  v.rig.reset();
  v.rig.playLoop("walk", 1, true);
  v.look?.reset();
  // a boss wears its own signature rim, not the boss crimson (a rare's and the Wyrm's are set by showpiece.ts)
  if (v.boss && v.rare === 0 && !v.wyrm && v.look) {
    const r = bossRim(arch.id);
    v.look.rimColor.setHex(r.color); v.look.rimStrength = r.strength; v.look.rimPulse = r.pulse;
  }
  v.bar.reset();
  v.maxHp = Math.max(1e-6, e.maxHp.toNumber());
  v.lastHp = v.simHp = v.runHp = e.hp.toNumber();
  v.owed = 0; v.doomed = false;
  v.knock.x = v.knock.v = 0; v.stretch = 0; v.age = 0; v.dead = 0; v.buried = 0; v.sinceShot = Infinity; v.turnHeld = false; v.path.set = false;
  resetTrack(v.foot);
  v.ik?.reset();
  if (v.bomb) v.bomb.scale.setScalar(1);
  if (v.hq) { v.body.position.set(0, 0, 0); v.body.rotation.set(0, HQ_YAW, 0); v.body.scale.setScalar(0); }
}

/** The model a new `id` unit wears: its HQ model (the LOD one while `far()`) — a stand-in's until its own ships — else NO_MODEL. */
export function enemyModel(id: string, far: () => boolean): string {
  const L = lib();
  const hq = hqEnemyName(enemyArtId(id));
  if (L && L.hq(hq)) return far() && L.hq(`${hq}_lod1`) ? `hq:${hq}_lod1` : `hq:${hq}`;
  return NO_MODEL;
}
/**
 * The far-zoom LOD rule for a spawn (`far` is EnemyViews.lodFar): a showpiece - a rare (chapter `rare` > 0) or the
 * Starfall Wyrm - never takes its `_lod1`; every other enemy follows the camera.
 */
export function lodPick(rare: number, wyrm: boolean, far: () => boolean): () => boolean { return rare > 0 || wyrm ? NEVER_FAR : far; }
const NEVER_FAR = (): boolean => false;
/** `enemyModel` when the unit's HQ model is not in: the view draws no body. */
export const NO_MODEL = "none";
